/**
 * Dominio propio de la tienda online.
 *
 * Toda la lógica pura del feature vive acá a propósito: `src/App.jsx` está en el
 * array PROTECTED de scripts/sync_gastify.mjs y NO viaja a Gastify por sync, así
 * que el port manual tiene que quedar en un puñado de líneas de consumo.
 * Este archivo sí viaja.
 *
 * Además hace reversible la decisión de infraestructura: la app no sabe quién
 * termina el TLS (Cloudflare for SaaS, Caddy o lo que venga).
 *
 * Spec: docs/ESPEC_DOMINIO_PROPIO_TIENDA.md
 * Test: tests/store-custom-domains.test.mjs
 */

/** Estados posibles de una fila de store_custom_domains. */
export const DOMAIN_ESTADOS = Object.freeze([
    'pendiente',
    'verificando',
    'activo',
    'suspendido',
    'error',
]);

/** Tope por organización: apex + www. Se sube por override desde superadmin. */
export const MAX_DOMINIOS_POR_ORG = 2;

const LABEL_RE = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;
const IPV4_RE = /^\d{1,3}(\.\d{1,3}){3}$/;
const SCHEME_RE = /^[a-z][a-z0-9+.-]*:\/\//i;

const MAX_HOSTNAME_LENGTH = 253;
const MAX_LABEL_LENGTH = 63;

/**
 * Normaliza lo que el cliente pega en el input a un hostname canónico.
 *
 * Es la puerta de entrada del feature: el índice único global de
 * store_custom_domains solo sirve si todos los caminos guardan la misma forma.
 * Saca esquema, path, query y puerto; baja a minúsculas; convierte IDN a
 * punycode y elimina el punto final.
 *
 * `www.` NO se saca: es un hostname distinto y lleva su propia fila.
 *
 * @param {unknown} raw
 * @returns {string} hostname normalizado, o '' si no se puede interpretar.
 */
export const normalizeHostname = (raw) => {
    if (typeof raw !== 'string') return '';

    let value = raw.trim();
    if (!value) return '';

    value = value.replace(SCHEME_RE, '');
    value = value.split(/[/?#]/)[0];
    if (!value) return '';

    let hostname;
    try {
        // La URL de WHATWG resuelve minúsculas, punycode y puerto de una.
        hostname = new URL(`http://${value}`).hostname;
    } catch {
        return '';
    }

    return hostname.replace(/\.$/, '');
};

const isIpHostname = (hostname) => hostname.startsWith('[') || IPV4_RE.test(hostname);

const isReservedHostname = (hostname, reservedSuffixes) => {
    if (hostname === 'localhost' || hostname.endsWith('.localhost')) return true;

    return reservedSuffixes.some((rawSuffix) => {
        const suffix = normalizeHostname(rawSuffix);
        if (!suffix) return false;
        // Comparación por label, no por substring: noimpulza.app NO es reservado.
        return hostname === suffix || hostname.endsWith(`.${suffix}`);
    });
};

const hasValidFormat = (hostname) => {
    if (hostname.length > MAX_HOSTNAME_LENGTH) return false;

    const labels = hostname.split('.');
    if (labels.length < 2) return false;

    return labels.every(
        (label) => label.length > 0 && label.length <= MAX_LABEL_LENGTH && LABEL_RE.test(label),
    );
};

/**
 * Valida un hostname candidato antes de guardarlo o de tocar al proveedor.
 *
 * Códigos de error estables ('vacio' | 'ip' | 'reservado' | 'formato'): la UI
 * los traduce, así que no se renombran sin tocar la UI.
 * Precedencia: vacio > ip > reservado > formato.
 *
 * @param {unknown} raw
 * @param {{ reservedSuffixes?: string[] }} [options] dominios de la propia marca.
 * @returns {{ ok: boolean, hostname: string, error: string|null }}
 */
export const validateHostname = (raw, options = {}) => {
    const reservedSuffixes = Array.isArray(options?.reservedSuffixes) ? options.reservedSuffixes : [];
    const hostname = normalizeHostname(raw);
    const fail = (error) => ({ ok: false, hostname, error });

    const rawTrimmed = typeof raw === 'string' ? raw.trim() : '';
    if (!rawTrimmed) return fail('vacio');
    if (!hostname) return fail('formato');

    if (isIpHostname(hostname)) return fail('ip');
    if (isReservedHostname(hostname, reservedSuffixes)) return fail('reservado');
    if (!hasValidFormat(hostname)) return fail('formato');

    return { ok: true, hostname, error: null };
};

/**
 * Decide si la SPA monta el ERP completo o solo el storefront.
 *
 * Un falso 'storefront' deja al cliente sin poder entrar al sistema, así que
 * ante cualquier duda (hostname vacío, lista de hosts propios vacía) devuelve
 * 'app'. El riesgo de mostrar el ERP de más se cubre en el guard de rutas.
 *
 * @param {unknown} hostname típicamente window.location.hostname.
 * @param {string[]} ownHosts hosts de la propia app (app.impulza.app, localhost…).
 * @returns {'app'|'storefront'}
 */
export const resolveStoreMode = (hostname, ownHosts) => {
    const host = normalizeHostname(hostname);
    if (!host) return 'app';

    const owned = (Array.isArray(ownHosts) ? ownHosts : [])
        .map((value) => normalizeHostname(value))
        .filter(Boolean);
    if (owned.length === 0) return 'app';

    const isOwn = owned.some((own) => host === own || host.endsWith(`.${own}`));
    return isOwn ? 'app' : 'storefront';
};

/**
 * Qué hace la app cuando entra por un dominio custom.
 *
 * 'render'     → mostrar la tienda acá.
 * 'redirect'   → mandar a otro lado (www → principal, o suspendido → URL impulza).
 * 'not_found'  → el dominio no puede mostrar la tienda en este host.
 *
 * Un no primario (www) solo redirige si el principal está activo
 * (`redirect_to` lo trae resolve_store_domain). Si el principal todavía no
 * tiene certificado, el www sirve la tienda: redirigir a un dominio que no
 * carga, o a la URL de Impulza, deja al comerciante sin canal de venta.
 *
 * @param {{ estado?: string, is_primary?: boolean, redirect_to?: string|null }|null|undefined} row
 * @returns {'render'|'redirect'|'not_found'}
 */
export const domainBehavior = (row) => {
    const estado = row && typeof row === 'object' ? row.estado : null;

    if (estado === 'activo') {
        return row.is_primary === false && normalizeHostname(row.redirect_to) ? 'redirect' : 'render';
    }
    if (estado === 'suspendido') return 'redirect';

    return 'not_found';
};

/**
 * Sugiere www para un dominio raíz.
 *
 * La mayoría de los paneles de DNS de Argentina (DonWeb, Hostinger, NIC con
 * hosting) no aceptan CNAME en la raíz; con www funciona en todos y la raíz se
 * redirige desde el registrador o se suma como segundo dominio.
 *
 * @param {unknown} raw
 * @returns {string} 'www.<dominio>' o '' si no aplica (ya es www o es un subdominio).
 */
export const suggestWwwHostname = (raw) => {
    const hostname = normalizeHostname(raw);
    if (!hostname || hostname.startsWith('www.')) return '';
    const labels = hostname.split('.');
    const segundoNivelAr = /^(com|net|org|gob|gov|edu|tur|int|mil|coop|mutual|musica|senasa|seg)\.ar$/;
    const esRaiz = labels.length === 2
        || (labels.length === 3 && segundoNivelAr.test(labels.slice(-2).join('.')));
    return esRaiz ? `www.${hostname}` : '';
};

/**
 * Única fuente de URLs de tienda del sistema.
 *
 * La usan el link de WhatsApp, el mail de confirmación, el rastreo del pedido,
 * el botón de compartir y el QR. Si cada lugar arma la URL por su cuenta, el
 * día que el cliente activa su dominio la mitad de los links quedan viejos.
 *
 * @param {{ slug?: string, domains?: Array<object>, appOrigin?: string }} params
 * @returns {string} URL base sin barra final.
 */
export const storeBaseUrl = ({ slug, domains = [], appOrigin = '' } = {}) => {
    const lista = Array.isArray(domains) ? domains : [];
    const primario = lista.find(
        (row) =>
            row
            && row.estado === 'activo'
            && row.is_primary !== false
            && normalizeHostname(row.hostname),
    );

    if (primario) return `https://${normalizeHostname(primario.hostname)}`;

    const origin = String(appOrigin || '').replace(/\/+$/, '');
    const safeSlug = String(slug || '').trim();
    if (!safeSlug) return origin;

    return `${origin}/tienda/${encodeURIComponent(safeSlug)}`;
};

/** Prefijo del registro TXT de verificación de propiedad. */
export const VERIFY_RECORD_PREFIX = '_impulza-verify';

/**
 * Nombre exacto del registro TXT que el cliente tiene que crear.
 *
 * Es la misma función que usa el wizard para mostrarlo y la Edge Function para
 * consultarlo. Si fueran dos strings distintos, el cliente cargaría el registro
 * bien y la verificación fallaría igual: el bug más frustrante posible, porque
 * él hizo todo correcto y nosotros le decimos que no.
 *
 * @param {unknown} rawHostname
 * @returns {string} '' si el hostname no se puede interpretar.
 */
export const verifyRecordName = (rawHostname) => {
    const hostname = normalizeHostname(rawHostname);
    if (!hostname) return '';
    return `${VERIFY_RECORD_PREFIX}.${hostname}`;
};

/**
 * Los dos registros DNS que el wizard le muestra al cliente para copiar.
 *
 * @param {{ hostname?: unknown, token?: string, originHost?: string }} params
 * @returns {Array<{ tipo: string, nombre: string, valor: string, ayuda: string }>}
 */
export const buildDnsRecords = ({ hostname, token, originHost } = {}) => {
    const host = normalizeHostname(hostname);
    const valorToken = String(token || '').trim();
    const origen = String(originHost || '').trim();

    if (!host || !valorToken || !origen) return [];

    return [
        {
            tipo: 'TXT',
            nombre: verifyRecordName(host),
            valor: valorToken,
            ayuda: 'Confirma que el dominio es tuyo. Se puede borrar una vez verificado.',
        },
        {
            tipo: 'CNAME',
            nombre: host,
            valor: origen,
            ayuda: 'Dirige las visitas de tu dominio a tu tienda.',
        },
    ];
};

/**
 * Valores TXT de una respuesta DNS-over-HTTPS.
 *
 * Los proveedores devuelven los TXT entre comillas, el mismo nombre puede tener
 * varios registros (SPF, DKIM y el nuestro conviviendo), y un TXT largo llega
 * partido en trozos entrecomillados que hay que concatenar.
 *
 * @param {unknown} doh respuesta JSON de un resolver DoH.
 * @returns {string[]} nunca null: lista vacía si no hay nada usable.
 */
export const parseDohTxt = (doh) => {
    if (!doh || typeof doh !== 'object') return [];
    if (doh.Status !== 0) return [];
    if (!Array.isArray(doh.Answer)) return [];

    return doh.Answer
        .filter((answer) => answer && answer.type === 16 && typeof answer.data === 'string')
        .map((answer) => {
            const trozos = answer.data.match(/"([^"]*)"/g);
            if (!trozos) return answer.data.trim();
            return trozos.map((t) => t.slice(1, -1)).join('');
        });
};

/**
 * ¿El TXT publicado prueba que el dominio es de quien lo reclama?
 *
 * La comparación es EXACTA sobre el valor completo del registro. Aceptar una
 * coincidencia parcial dejaría que una organización valide el dominio de otra
 * pegando el token ajeno dentro de un TXT más largo.
 *
 * @param {unknown} doh respuesta del resolver.
 * @param {unknown} expectedToken
 * @returns {boolean}
 */
export const txtMatches = (doh, expectedToken) => {
    const esperado = String(expectedToken || '').trim();
    if (!esperado) return false;

    return parseDohTxt(doh).some((valor) => valor.trim() === esperado);
};

/**
 * Puede esta organización dar de alta un dominio más.
 *
 * Se chequea ANTES de tocar la API del proveedor: cada hostname de más es cuota
 * y costo variable. El duplicado se evalúa antes que el tope para que el
 * mensaje sea el útil ("ya lo tenés cargado") y no "llegaste al límite".
 *
 * @param {{ hostname?: unknown, existing?: Array<object>, max?: number, reservedSuffixes?: string[] }} params
 * @returns {{ ok: boolean, error: string|null }} error: código de validateHostname | 'duplicado' | 'tope'.
 */
export const canAddDomain = ({
    hostname,
    existing = [],
    max = MAX_DOMINIOS_POR_ORG,
    reservedSuffixes = [],
} = {}) => {
    const validacion = validateHostname(hostname, { reservedSuffixes });
    if (!validacion.ok) return { ok: false, error: validacion.error };

    const actuales = (Array.isArray(existing) ? existing : [])
        .map((row) => normalizeHostname(row?.hostname))
        .filter(Boolean);

    if (actuales.includes(validacion.hostname)) return { ok: false, error: 'duplicado' };
    if (actuales.length >= max) return { ok: false, error: 'tope' };

    return { ok: true, error: null };
};
